import React, { useEffect, useMemo, useRef, useState } from "react";

import { FileX } from "lucide-react";

import BriefingMatrixSkeleton from "./BriefingMatrixSkeleton";
import {
  transformDigestDataToAccounts,
  extractSectionsFromDigestData,
} from "./dataAdapter";
import { WatchlistSummaryMatrix } from "./WatchlistSummaryMatrix";
import PublicDataTipBanner from "../../Pages/AlwaysOnChannel/Dashboard/components/PublicDataTipBanner";
import { useAppSelector } from "../../Store/Hooks";
import {
  WatchlistSummaryAccount,
  WatchlistSummarySection,
} from "../../Types/WatchlistSummary";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

import "./style.scss";

const EMPTY_ACCOUNTS_LIST: any[] = [];
let watchlistBriefingApiCycle = 0;

const DEFAULT_MATRIX_SECTIONS: WatchlistSummarySection[] = [
  {
    id: "about",
    name: "About Company",
    enabled: true,
    display_order: 1,
    icon_name: "about",
  },
  {
    id: "financial",
    name: "Financial Overview",
    enabled: true,
    display_order: 2,
    icon_name: "financial",
  },
  {
    id: "news",
    name: "News Analysis",
    enabled: true,
    display_order: 3,
    icon_name: "news",
  },
  {
    id: "leadership",
    name: "Leadership Team",
    enabled: true,
    display_order: 4,
    icon_name: "leadership",
  },
  {
    id: "legal",
    name: "Legal & Compliance",
    enabled: true,
    display_order: 5,
    icon_name: "legal",
  },
  {
    id: "social",
    name: "Social Media",
    enabled: true,
    display_order: 6,
    icon_name: "social",
  },
  {
    id: "traffic",
    name: "Digital Traffic",
    enabled: true,
    display_order: 7,
    icon_name: "traffic",
  },
  {
    id: "growth",
    name: "Growth Signals",
    enabled: true,
    display_order: 8,
    icon_name: "growth",
  },
  {
    id: "stack",
    name: "Technology Stack",
    enabled: true,
    display_order: 9,
    icon_name: "stack",
  },
];

const getAccountNameForSort = (account: any) =>
  String(account?.name || account?.accountName || account?.account_name || "")
    .trim()
    .toLowerCase();

const getSponsorNameForSort = (account: any) =>
  String(account?.primary_sponsors || "").trim() || "Unassigned";

const isUnassignedForSort = (account: any) => {
  const primarySponsorId = String(account?.primary_sponsors_id || "").trim();
  const secondarySponsorIds = account?.secondary_sponsors_id
    ? String(account.secondary_sponsors_id)
        .split(",")
        .map((id: string) => id.trim())
        .filter(Boolean)
    : [];

  return !primarySponsorId && secondarySponsorIds.length === 0;
};

const filterAccountsByCustomerIds = (
  accounts: WatchlistSummaryAccount[],
  customerIds: string[],
  accountsById: Map<string, any>,
  accountsByName: Map<string, any>
): WatchlistSummaryAccount[] => {
  if (customerIds.length === 0) return accounts;

  const idSet = new Set(
    customerIds.map(id => String(id).trim()).filter(Boolean)
  );

  return accounts.filter(account => {
    const accountId = String(account.accountId || "").trim();
    if (accountId && idSet.has(accountId)) return true;

    const nameKey = String(account.accountName || "")
      .trim()
      .toLowerCase();
    const catalogAccount = accountsByName.get(nameKey);
    if (catalogAccount) {
      const catalogId = String(
        catalogAccount.customer_id || catalogAccount.id || ""
      ).trim();
      if (catalogId && idSet.has(catalogId)) return true;
    }

    const catalogById = accountId ? accountsById.get(accountId) : undefined;
    if (catalogById) {
      const catalogId = String(
        catalogById.customer_id || catalogById.id || ""
      ).trim();
      if (catalogId && idSet.has(catalogId)) return true;
    }

    return false;
  });
};

const skeletonSections = DEFAULT_MATRIX_SECTIONS.filter(
  s => s.enabled && s.id !== "summary"
);

interface WatchlistSummaryProps {
  customerIds?: string[];
  userId?: string;
  /** True when Any Portfolio is filtered to accounts with no sponsor (Unassigned). */
  isUnassignedPortfolio?: boolean;
  /** Any Portfolio dropdown set to "All" (briefing uses user_ids, not userId). */
  includeAllPortfolios?: boolean;
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  selectionPanelRef?: React.RefObject<HTMLElement | null>;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
}

/**
 * Example component showing how to use WatchlistSummaryMatrix with your data.json file
 */
export const WatchlistSummary: React.FC<WatchlistSummaryProps> = ({
  customerIds = [],
  userId,
  isUnassignedPortfolio = false,
  includeAllPortfolios = false,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  selectionPanelRef,
  onSaveAsWatchlist,
}) => {
  const {
    accounts: { data: accountsData },
  } = useAppSelector(state => state.ops);
  const { watchListBriefing, watchListBriefingError, generateBriefingLoading } =
    useAppSelector(state => state.briefDocument);

  const [summaryData, setSummaryData] = useState<WatchlistSummaryAccount[]>([]);
  const [sections, setSections] = useState<WatchlistSummarySection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [animationCycle, setAnimationCycle] = useState(
    watchlistBriefingApiCycle
  );

  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const organization = loggedInUser?.tenantDomain || "";
  const accountsList = accountsData?.items ?? EMPTY_ACCOUNTS_LIST;

  const accountsById = useMemo(
    () =>
      new Map(
        accountsList.map((account: any) => [
          String(account.customer_id || account.id || "").trim(),
          account,
        ])
      ),
    [accountsList]
  );

  const accountsByName = useMemo(
    () =>
      new Map(
        accountsList.map((account: any) => [
          String(account.name || account.account_name || "")
            .trim()
            .toLowerCase(),
          account,
        ])
      ),
    [accountsList]
  );

  const customerIdsKey = useMemo(
    () =>
      JSON.stringify(
        [...customerIds]
          .map(id => String(id).trim())
          .filter(Boolean)
          .sort()
      ),
    [customerIds]
  );

  const hasScopedInput = useMemo(
    () =>
      customerIds.length > 0 ||
      Boolean(userId?.trim()) ||
      isUnassignedPortfolio ||
      includeAllPortfolios,
    [customerIds.length, userId, isUnassignedPortfolio, includeAllPortfolios]
  );

  const prevProcessedBriefingKeyRef = useRef<string>("");
  const prevGenerateBriefingLoadingRef = useRef(generateBriefingLoading);
  const clearUnscopedState = () => {
    prevProcessedBriefingKeyRef.current = "";
    setSummaryData(prev => (prev.length === 0 ? prev : []));
    setSections(prev => (prev.length === 0 ? prev : []));
    setIsLoading(prev => (prev === false ? prev : false));
  };

  useEffect(() => {
    // Trigger bubble animation only when watch-list-briefing API call completes.
    const apiJustCompleted =
      prevGenerateBriefingLoadingRef.current && !generateBriefingLoading;

    if (apiJustCompleted && watchListBriefing) {
      watchlistBriefingApiCycle += 1;
      setAnimationCycle(watchlistBriefingApiCycle);
    }

    prevGenerateBriefingLoadingRef.current = generateBriefingLoading;
  }, [generateBriefingLoading, watchListBriefing]);

  useEffect(() => {
    if (!hasScopedInput) {
      clearUnscopedState();
      return;
    }

    if (!watchListBriefing) {
      prevProcessedBriefingKeyRef.current = "";
      return;
    }

    const resultAccountIds = (watchListBriefing?.results || [])
      .map((item: any) => String(item.account_id || "").trim())
      .filter(Boolean)
      .sort()
      .join(",");
    const processKey = `${customerIdsKey}|${String(watchListBriefing?.date || "")}|${resultAccountIds}`;
    if (processKey === prevProcessedBriefingKeyRef.current) return;
    prevProcessedBriefingKeyRef.current = processKey;

    const scopedCustomerIds =
      customerIdsKey && customerIdsKey !== "[]"
        ? (JSON.parse(customerIdsKey) as string[])
        : [];

    // Transform the API response to the component format
    try {
      if (
        !watchListBriefing.results ||
        !Array.isArray(watchListBriefing.results)
      ) {
        setSummaryData(prev => (prev.length === 0 ? prev : []));
        setSections(prev => (prev.length === 0 ? prev : []));
        setIsLoading(false);
        return;
      }

      const briefingData = watchListBriefing.results.flatMap(
        (item: any) => item.digests || []
      );

      const transformedDigestAccounts = transformDigestDataToAccounts(
        briefingData as any,
        watchListBriefing.organization_name || "Your Organization Name"
      );

      const accountsWithoutDigest = watchListBriefing.results
        .filter(
          (item: any) =>
            !Array.isArray(item?.digests) || item.digests.length === 0
        )
        .map((item: any) => ({
          accountName:
            item.customer_name || item.account_name || "Unknown Account",
          accountId: String(item.account_id),
          organizationName:
            watchListBriefing.organization_name || "Your Organization Name",
          accountBriefLine: "No briefing for the selected date.",
          hasData: false,
          hasSelectedDateData: false,
          sectionsData: [],
        }));

      const mergedAccounts = [
        ...transformedDigestAccounts,
        ...accountsWithoutDigest,
      ];

      const scopedAccounts = filterAccountsByCustomerIds(
        mergedAccounts,
        scopedCustomerIds,
        accountsById,
        accountsByName
      );

      const transformedAccounts = scopedAccounts.sort((left, right) => {
        const leftSourceAccount =
          accountsById.get(String(left.accountId || "").trim()) ||
          accountsByName.get(
            String(left.accountName || "")
              .trim()
              .toLowerCase()
          );
        const rightSourceAccount =
          accountsById.get(String(right.accountId || "").trim()) ||
          accountsByName.get(
            String(right.accountName || "")
              .trim()
              .toLowerCase()
          );

        const leftUnassigned = leftSourceAccount
          ? isUnassignedForSort(leftSourceAccount)
          : false;
        const rightUnassigned = rightSourceAccount
          ? isUnassignedForSort(rightSourceAccount)
          : false;

        if (leftUnassigned !== rightUnassigned) {
          return leftUnassigned ? -1 : 1;
        }

        const sponsorCompare = getSponsorNameForSort(
          leftSourceAccount
        ).localeCompare(getSponsorNameForSort(rightSourceAccount), undefined, {
          sensitivity: "base",
        });

        if (sponsorCompare !== 0) {
          return sponsorCompare;
        }

        return getAccountNameForSort(leftSourceAccount || left).localeCompare(
          getAccountNameForSort(rightSourceAccount || right),
          undefined,
          { sensitivity: "base" }
        );
      });

      const extractedSections = extractSectionsFromDigestData(
        briefingData as any
      );

      setSummaryData(transformedAccounts);
      setSections(
        extractedSections.length > 0
          ? extractedSections
          : DEFAULT_MATRIX_SECTIONS
      );
      setIsLoading(false);
    } catch (error) {
      console.error("Error transforming watchlist briefing data:", error);
      setSummaryData(prev => (prev.length === 0 ? prev : []));
      setSections(prev => (prev.length === 0 ? prev : []));
      setIsLoading(false);
    }
  }, [watchListBriefing, customerIdsKey, accountsList, hasScopedInput]);

  // Handle empty state when no scope is selected or briefing data is cleared
  useEffect(() => {
    if (!hasScopedInput) {
      clearUnscopedState();
      return;
    }
    if (!watchListBriefing && !generateBriefingLoading) {
      setIsLoading(prev => (prev === false ? prev : false));
    }
  }, [watchListBriefing, hasScopedInput, generateBriefingLoading]);

  const handleShareClick = () => {
    // Open share modal
  };

  const handleConfigureClick = () => {
    // Open configure modal
  };

  const handleGenerateClick = () => {
    onSaveAsWatchlist?.(highlightedAccountIds);
  };

  const isLoadingData = generateBriefingLoading;
  const hasApiResponseResults =
    Array.isArray(watchListBriefing?.results) &&
    watchListBriefing.results.length > 0;

  const isMatrixEmpty =
    !hasScopedInput ||
    (summaryData.length === 0 &&
      (customerIds.length > 0 || !hasApiResponseResults));

  if (isLoadingData) {
    return <BriefingMatrixSkeleton sections={skeletonSections} />;
  }

  // Show empty state only when response has no usable rows (or API errors).
  // If results exist, render the matrix so users can still see "no briefing"
  // account rows instead of a fully empty screen.
  if (isMatrixEmpty || watchListBriefingError) {
    const showUnassignedEmpty = isUnassignedPortfolio && isMatrixEmpty;

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileX className="w-8 h-8 text-gray-400" />
        </div>
        {showUnassignedEmpty ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Unassigned Accounts
            </h3>
            <p className="text-gray-600 max-w-md">
              Currently there are no unassigned accounts. You can select any
              executive sponsor from the drop down to review account briefings
              for their portfolio.
            </p>
          </>
        ) : includeAllPortfolios ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Intelligence Briefings Generated
            </h3>
            <p className="text-gray-600 max-w-md">
              No public data briefings were returned for accounts across all
              executive sponsor portfolios.
            </p>
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Intelligence Briefings Generated
            </h3>
            <p className="text-gray-600 max-w-md">
              This Executive Sponsor currently has no assigned portfolio
              accounts.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PublicDataTipBanner />
      <div className="flex min-h-0 flex-1 flex-col">
        <WatchlistSummaryMatrix
          summaryData={summaryData}
          sections={sections}
          selectedDate={watchListBriefing?.date}
          animationCycle={animationCycle}
          highlightedAccountIds={highlightedAccountIds}
          onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
          selectionPanelRef={selectionPanelRef}
          watchlistName="Sample Watchlist"
          organizationName={organization}
          isLoading={isLoading}
          onShareClick={handleShareClick}
          onConfigureClick={handleConfigureClick}
          onGenerateClick={handleGenerateClick}
        />
      </div>
    </div>
  );
};

export default WatchlistSummary;
